import React, { useEffect, useState } from 'react';
import NumberOfOffer from 'atomic/number-of-offer';
import Link from 'atomic/link';
import Badge from 'atomic/badge';
import Rating from 'atomic/rating';
import classNames from 'classnames';
import { CarouselProps } from 'react-multi-carousel';
import WidgetPhotosGridTeaserProps from 'modules/shared/widgets/components/widget-photos-grid-teaser/widget-photos-grid-teaser.props';
import WidgetCarouselDesktopBasic
    from '../../../../widget-carousel/widget-carousel-deskktop/components/widget-carousel-desktop-basic/widget-carousel-desktop-basic';
import AlertDiscountPriceInfo from 'modules/shared/components/alert-discount-price-info';
import WidgetAttributesTable from 'modules/shared/widgets/shared/widget-attributes-table';
import { prepareOfferDataForDiscount } from 'modules/shared/helpers/prepare-offer-data-for-discount';
import WidgetProductData from 'modules/shared/widgets/interfaces/widgets-data/widget-product-data.interface';
import ProductBoxPrice from 'modules/shared/components/product/components/product-box-price';
import { isMonoOffer } from 'helpers/offers-prepare';
import OfferLabel from 'modules/shared/modules/product/components/offer-label';
import { getMonoOfferLabel } from 'helpers/offer-label';
import { SeoLinkRelEnum } from 'modules/seo-module/interfaces/seo-link-rel.enum';
import { LinkTarget } from 'atomic/link/link-target.enum';

import './widget-photos-grid-teaser-desktop-slider.scss';

interface WidgetPhotosGridTeaserSliderProps extends WidgetPhotosGridTeaserProps {
    isSliderSmall: boolean;
}

const WidgetPhotosGridTeaserDesktopSlider: React.FC<WidgetPhotosGridTeaserSliderProps> = ({ config, data, isSliderSmall }) => {
    const [WidgetPhotosGridTeaserCarouselComponent, setWidgetCarousel] = useState<React.ComponentType<CarouselProps>>(() => WidgetCarouselDesktopBasic);
    const numberOfItems = isSliderSmall ? 3 : 5;
    const { items } = data;

    useEffect(() => {
        import('react-multi-carousel').then(mod => {
            setWidgetCarousel(() => mod.default);
        });
    }, []);

    const responsive = {
        desktop: {
            breakpoint: { max: 5000, min: 0 },
            items: numberOfItems
        }
    };

    return (
        <div className={classNames({
            'widget-photo-grid-teaser-carousel': true,
            'widget-photo-grid-teaser-carousel__small': numberOfItems === 3
        })}
        >
            <WidgetPhotosGridTeaserCarouselComponent
                responsive={responsive}
                swipeable={false}
                draggable={false}
                slidesToSlide={numberOfItems}
                renderButtonGroupOutside={true}
                arrows={true}
                autoPlay={config.autoPlay}
                className="w-carousel-d__list"
                itemClass={`w-carousel-d__list__item w-${numberOfItems}`}
            >
                {(items || []).map((item: WidgetProductData['items'][0]) => {
                    const {
                        discount,
                        minPrice30Days,
                        price,
                        offersCount,
                        prevPrice,
                        showDiscount
                    } = prepareOfferDataForDiscount(
                        item.minPrice,
                        item.prevPrice,
                        item.offers.items,
                        item.offers.count
                    );

                    const offerLabel = getMonoOfferLabel(item);

                    return (
                        <div key={item.id} className="slider__list__item-wrapper">
                            <div className="slider__list__item-wrapper__stars">
                                <Rating size="sm" opinions={item.opinions} />
                            </div>
                            <Link
                                className="slider__list__item-content"
                                title={isMonoOffer(item) ? item.offers.items[0].dealer.name : item.name}
                                href={isMonoOffer(item) ? item.offers.items[0].url : item.url}
                                rel={isMonoOffer(item) ? SeoLinkRelEnum.NOOPENER_NOREFERRER : void 0}
                                target={isMonoOffer(item) ? LinkTarget.BLANK : void 0}
                            >
                                <div className={'slider__list__item-content__image'}>
                                    <img
                                        src={item.photo}
                                        alt={item.name}
                                        title={item.name}
                                        loading={config.lazyLoad ? 'lazy' : 'eager'}
                                    />
                                    {showDiscount && (
                                        <div className="slider__list__item-content__image__badges">
                                            <Badge color="red">
                                                <span>{discount}%</span>
                                            </Badge>
                                        </div>
                                    )}
                                </div>
                                <div className="slider__list__item-content__title">
                                    <h3>
                                        {item.name}
                                    </h3>
                                </div>
                                <div className="slider__list__item-content__stars-placeholder" />
                                <div className="slider__list__item-content__price">
                                    <ProductBoxPrice
                                        price={price}
                                        prevPrice={prevPrice}
                                        offersCount={offersCount}
                                        discount={discount}
                                    />
                                </div>
                                {showDiscount && (
                                    <div className="slider__list__item-content__discount-info">
                                        <AlertDiscountPriceInfo paddingTop={0} paddingBottom={12} minPrice30Days={minPrice30Days} />
                                    </div>
                                )}
                                {!showDiscount && item.attributes && (
                                    <div className="slider__list__item-content__attributes">
                                        <WidgetAttributesTable attributes={item.attributes} rowsNumber={3} />
                                    </div>
                                )}
                                <div className="slider__list__item-content__number-of-offer">
                                    <NumberOfOffer offers={item.offers} />
                                </div>
                            </Link>
                            {offerLabel && (
                                <div className="slider__list__item-wrapper__label">
                                    <OfferLabel label={offerLabel} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </WidgetPhotosGridTeaserCarouselComponent>
        </div>
    );
};

export default WidgetPhotosGridTeaserDesktopSlider;
